import '.d{display:flex;}.ssr-plugin'
import '.e{flex-direction:column;}.ssr-plugin'
import '.t{background-color:var(--palette-white);}.ssr-plugin'
import '.i9{border-radius:var(--border-radius-image);}.ssr-plugin'
import '.ia{padding:3.5rem 2rem;}.ssr-plugin'
import '.r{align-items:center;}.ssr-plugin'
import '.b4{text-align:center;}.ssr-plugin'
import '.n{width:100%;}.ssr-plugin'
import '.am{height:100%;}.ssr-plugin'
import '.hh{box-sizing:border-box;}.ssr-plugin'
import '.ib{width:12.5rem;}.ssr-plugin'
import '.a{display:grid;}.ssr-plugin'
import '.ic{grid-template-rows:auto 1fr auto;}.ssr-plugin'
import '.an{flex:1;}.ssr-plugin'
import '.ad{justify-content:center;}.ssr-plugin'
import '.id{padding:0.75rem 0;}.ssr-plugin'
import '.cs{justify-content:flex-end;}.ssr-plugin'
import '.q{flex-direction:row;}.ssr-plugin'
import '.g{gap:1rem;}.ssr-plugin'
import '.aj{flex-shrink:0;}.ssr-plugin'
import '.ag{position:relative;}.ssr-plugin'
import '.ie{transition:transform 0.25s ease, box-shadow 0.25s ease;}.ssr-plugin'
import '.if{background-color:lightgray;}.ssr-plugin'
import '.ig{background-size:100px 100px, auto;}.ssr-plugin'
import '.ih{background-repeat:repeat, no-repeat;}.ssr-plugin'
import '.ii{background-position:0% 0%, 0% 0%;}.ssr-plugin'
import '.ij{background-blend-mode:overlay, normal;}.ssr-plugin'
import { Button } from '@app/components/core/button/Button'
import { Image } from '@app/components/core/image/Image'
import { Paragraph } from '@app/components/core/typography/Paragraph'
import { Title } from '@app/components/core/typography/Title'
import useMediaQuery from '@app/util/useMediaQuery'
import contactCardTexture from '@app/assets/images/background-contact-card.png'
import { type Style, styled } from 'inlines'
import { SafeHtml } from '@app/components/core/safeHtml/SafeHtml'
import Column from '@app/components/layout/Column'

export type ContactPerson = {
  imageSrc: string
  imageAlt: string
  name: string
  role: string
  buttonLabel: string
  buttonLink: string
}

export type ContactCardProps = {
  title: string
  description?: string
  htmlContent?: string
  contactPeople: ContactPerson[]
  style?: Style
}

const CONTACT_PERSON_CARD_WIDTH = '16.5rem'
const STACK_PEEK_AT_THREE = '10rem'
const STACK_PEEK_AT_FIVE = '3rem'
const STACK_COUNT_FOR_WIDEST_PEEK = 3
const STACK_COUNT_FOR_TIGHTEST_PEEK = 5

const getHorizontalStackPeek = (count: number): string => {
  if (count <= STACK_COUNT_FOR_WIDEST_PEEK) {
    return STACK_PEEK_AT_THREE
  }

  if (count >= STACK_COUNT_FOR_TIGHTEST_PEEK) {
    return STACK_PEEK_AT_FIVE
  }

  const minPeek = parseFloat(STACK_PEEK_AT_THREE)
  const maxPeek = parseFloat(STACK_PEEK_AT_FIVE)
  const progress =
    (count - STACK_COUNT_FOR_WIDEST_PEEK) /
    (STACK_COUNT_FOR_TIGHTEST_PEEK - STACK_COUNT_FOR_WIDEST_PEEK)
  const peek = minPeek + progress * (maxPeek - minPeek)

  return `${peek}rem`
}

const getHorizontalStackOverlap = (peek: string) =>
  `(${CONTACT_PERSON_CARD_WIDTH} - ${peek})`

const ContactPersonCard = ({
  person,
  style,
}: {
  person: ContactPerson
  style?: Style
}) => (
  <styled.div className="d e t i9 ia r b4 n am hh"
    style={{
      ...style,
    }}
  >
    <Image
      src={person.imageSrc}
      alt={person.imageAlt}
      resizeWidth={144}
      style={{
        width: '9rem',
        height: '9rem',
        borderRadius: '50%',
        objectFit: 'cover',
        objectPosition: 'center top',
        marginBottom: '0.75rem',
      }}
    />
    <div className="ib a ic an"
    >
      <Title.H4
        style={{
          whiteSpace: 'pre-line',
          marginBottom: 0,
        }}
      >
        {person.name}
      </Title.H4>
      <div className="d r ad b4 id"
      >
        <Paragraph.Medium
          style={{
            marginBottom: 0,
          }}
        >
          {person.role}
        </Paragraph.Medium>
      </div>
      <Button
        size="medium"
        variant="primary"
        label={person.buttonLabel}
        to={person.buttonLink}
      />
    </div>
  </styled.div>
)

const ContactPeopleLayout = ({
  contactPeople,
  isMobile,
}: {
  contactPeople: ContactPerson[]
  isMobile: boolean
}) => {
  const count = contactPeople.length

  if (count === 0) {
    return null
  }

  if (isMobile) {
    return (
      <Column>
        {contactPeople.map((person) => (
          <ContactPersonCard key={person.name} person={person} />
        ))}
      </Column>
    )
  }

  if (count === 1) {
    return (
      <div className="d cs"
        style={{
          width: CONTACT_PERSON_CARD_WIDTH,
        }}
      >
        <ContactPersonCard person={contactPeople[0]} />
      </div>
    )
  }

  if (count === 2) {
    return (
      <div className="d q g cs"
      >
        {contactPeople.map((person) => (
          <div className="aj"
            key={person.name}
            style={{
              width: CONTACT_PERSON_CARD_WIDTH,}}
          >
            <ContactPersonCard person={person} />
          </div>
        ))}
      </div>
    )
  }

  const stackPeek = getHorizontalStackPeek(count)
  const stackOverlap = getHorizontalStackOverlap(stackPeek)

  return (
    <div className="d q cs"
      style={{
        width: `calc(${CONTACT_PERSON_CARD_WIDTH} + ${count - 1} * ${stackPeek})`,
      }}
    >
      {contactPeople.map((person, index) => (
        <styled.div className="ag aj ie i9"
          key={person.name}
          style={{
            width: CONTACT_PERSON_CARD_WIDTH,
            marginLeft: index === 0 ? 0 : `calc(-1 * ${stackOverlap})`,
            zIndex: index + 1,
            '&:hover': {
              zIndex: count + 1,
              transform: 'translateY(-0.5rem)',
              boxShadow: '0 12px 32px rgba(0, 0, 0, 0.18)',
            },
          }}
        >
          <ContactPersonCard person={person} />
        </styled.div>
      ))}
    </div>
  )
}

export const ContactCard = ({
  title,
  description,
  htmlContent,
  style,
  contactPeople,
}: ContactCardProps) => {
  const isMobile = useMediaQuery()

  return (
    <styled.div className="am if ig ih ii ij"
      style={{
        backgroundImage: `url(${contactCardTexture}), linear-gradient(177deg, #FFF 13.41%, #FBEBD1 66.89%, #FFCB93 93.89%, #F1A782 115.68%)`,
        borderRadius: isMobile ? '0.5rem' : '3rem',
        padding: isMobile ? '0.5rem' : '4.5rem',
        ...style,
      }}
    >
      <div className="a r"
        style={{
          gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) auto',
          gap: isMobile ? '2rem' : '9rem',}}
      >
        <styled.div>
          {title && <Title.H2>{title}</Title.H2>}
          {description && <Paragraph.Medium>{description}</Paragraph.Medium>}
          {htmlContent && <SafeHtml html={htmlContent} />}
        </styled.div>

        <ContactPeopleLayout
          contactPeople={contactPeople}
          isMobile={isMobile}
        />
      </div>
    </styled.div>
  )
}
